import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, ListTree, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { blogRequest, formatDate, imageUrl } from "../lib/blogApi";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [relatedPosts, setRelatedPosts] = useState([]);
  const articleContentRef = useRef(null);

  const enhancedContent = useMemo(
    () => prepareArticleContent(post?.contentHtml || "", imageUrl),
    [post]
  );

  useEffect(() => {
    const root = articleContentRef.current;
    if (!root) return;

    const images = root.querySelectorAll("img");
    const hideBrokenImage = (event) => {
      event.currentTarget.style.display = "none";
    };

    images.forEach((img) => img.addEventListener("error", hideBrokenImage));
    return () =>
      images.forEach((img) => img.removeEventListener("error", hideBrokenImage));
  }, [enhancedContent.html]);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError("");
    setPost(null);
    setRelatedPosts([]);

    blogRequest(`/posts/${encodeURIComponent(slug || "")}`)
      .then(({ post: item }) => {
        if (!active) return;
        if (!item) throw new Error("Blog post was not found.");

        setPost(item);

        blogRequest("/posts?limit=30")
          .then((result) => {
            if (!active) return;

            const others = (result.posts || []).filter(
              (candidate) =>
                candidate._id !== item._id && candidate.slug !== item.slug
            );
            const currentTags = (item.tags || []).map((tag) =>
              String(tag).toLowerCase()
            );
            const matched = others.filter((candidate) =>
              (candidate.tags || []).some((tag) =>
                currentTags.includes(String(tag).toLowerCase())
              )
            );

            setRelatedPosts((matched.length ? matched : others).slice(0, 3));
          })
          .catch(() => {
            if (active) setRelatedPosts([]);
          });

        document.title = item.pageTitle || `${item.title} | RankifyDev`;
        setMeta("description", item.metaDescription || item.excerpt || "");
        setMeta("keywords", (item.keywords || []).join(", "));
        setMeta("og:title", item.pageTitle || item.title, "property");
        setMeta(
          "og:description",
          item.metaDescription || item.excerpt || "",
          "property"
        );
        setMeta("og:type", "article", "property");

        if (item.featureImage) {
          setMeta("og:image", imageUrl(item.featureImage), "property");
        }

        setCanonical(`${window.location.origin}/blog/${item.slug}`);
      })
      .catch((err) => {
        if (active) setError(err.message || "Could not load this article.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  // Scroll to the selected TOC heading without using a CSS selector.
  // getElementById supports IDs that begin with a number.
  useEffect(() => {
    const handleHashChange = () => {
      if (!window.location.hash) return;

      let id;
      try {
        id = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }

      window.requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    };

    window.addEventListener("hashchange", handleHashChange);
    if (window.location.hash) handleHashChange();

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [enhancedContent.html]);

  return (
    <div className="App min-h-screen">
      <Header />

      <main className="pt-20">
        {loading && (
          <div className="mx-auto max-w-3xl px-4 py-32 text-center text-slate-500">
            Loading article...
          </div>
        )}

        {!loading && error && (
          <div className="mx-auto max-w-3xl px-4 py-32 text-center">
            <h1 className="text-3xl font-bold text-[hsl(var(--brand-navy))]">
              Article not found
            </h1>
            <p className="mt-3 text-slate-600">{error}</p>
            <Link
              to="/blog"
              className="mt-6 inline-flex rounded-full bg-brand px-5 py-3 font-semibold text-white"
            >
              Back to blog
            </Link>
          </div>
        )}

        {!loading && post && (
          <>
            <section className="brand-gradient-soft border-b border-black/5 py-12 sm:py-16">
              <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3"
                >
                  <ArrowLeft size={16} /> All articles
                </Link>

                <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                  {post.publishedAt && (
                    <span className="inline-flex items-center gap-2">
                      <CalendarDays size={16} />
                      {formatDate(post.publishedAt)}
                    </span>
                  )}

                  {(post.tags || []).slice(0, 4).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-[hsl(var(--brand-violet)/.15)] bg-white/80 px-3 py-1 text-xs font-semibold text-brand-deep"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="mt-5 text-3xl font-bold leading-tight text-[hsl(var(--brand-navy))] sm:text-4xl lg:text-5xl">
                  {post.title}
                </h1>

                {post.excerpt && (
                  <p className="mt-5 text-lg leading-8 text-slate-600">
                    {post.excerpt}
                  </p>
                )}
              </div>
            </section>

            <article className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
              {post.featureImage && (
                <img
                  src={imageUrl(post.featureImage)}
                  alt={post.title}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                  className="mb-10 max-h-[520px] w-full rounded-2xl object-cover card-shadow"
                />
              )}

              {enhancedContent.headings.length > 0 && (
                <nav
                  aria-label="Table of contents"
                  className="mb-10 rounded-2xl border border-[hsl(var(--brand-violet)/.15)] bg-white p-5 sm:p-6"
                >
                  <h2 className="flex items-center gap-2 text-lg font-bold text-[hsl(var(--brand-navy))]">
                    <ListTree size={19} /> In this article
                  </h2>

                  <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-6">
                    {enhancedContent.headings.map((heading) => (
                      <li
                        key={heading.id}
                        className={heading.level > 2 ? "ml-4" : ""}
                      >
                        <a
                          className="text-brand hover:underline"
                          href={`#${encodeURIComponent(heading.id)}`}
                          onClick={(event) => {
                            event.preventDefault();
                            window.history.replaceState(
                              null,
                              "",
                              `#${encodeURIComponent(heading.id)}`
                            );
                            const target = document.getElementById(heading.id);
                            if (target) {
                              target.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          }}
                        >
                          {heading.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}

              <div
                ref={articleContentRef}
                className="blog-html-content"
                dangerouslySetInnerHTML={{ __html: enhancedContent.html }}
              />

              {post.images?.length > 0 && (
                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                  {post.images.map((img, index) => (
                    <img
                      key={`${img}-${index}`}
                      src={imageUrl(img)}
                      alt={`${post.title} image ${index + 1}`}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                      className="w-full rounded-xl object-cover"
                    />
                  ))}
                </div>
              )}

              <div className="mt-12 border-t border-black/10 pt-6">
                <p className="text-sm font-semibold text-[hsl(var(--brand-navy))]">
                  Topics
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  {(post.tags || []).map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog?tag=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-lilac px-3 py-1.5 text-xs font-medium text-brand-deep"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <Link
                  to="/blog"
                  className="mt-8 inline-flex items-center gap-2 font-semibold text-brand"
                >
                  <ArrowLeft size={16} /> Back to all articles
                </Link>
              </div>

              {relatedPosts.length > 0 && (
                <section className="mt-14 border-t border-black/10 pt-10">
                  <p className="text-sm font-bold uppercase tracking-wider text-brand">
                    Keep reading
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-[hsl(var(--brand-navy))] sm:text-3xl">
                    Related articles
                  </h2>

                  <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {relatedPosts.map((related) => (
                      <article
                        key={related._id}
                        className="overflow-hidden rounded-2xl border border-black/5 bg-white card-shadow"
                      >
                        <Link to={`/blog/${related.slug}`} className="block">
                          <div className="aspect-[16/10] overflow-hidden bg-lilac">
                            {related.featureImage ? (
                              <img
                                src={imageUrl(related.featureImage)}
                                alt={related.title}
                                loading="lazy"
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center brand-gradient-soft text-3xl font-bold text-brand/40">
                                R.
                              </div>
                            )}
                          </div>

                          <div className="p-5">
                            <h3 className="font-bold leading-snug text-[hsl(var(--brand-navy))]">
                              {related.title}
                            </h3>
                            {related.excerpt && (
                              <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">
                                {related.excerpt}
                              </p>
                            )}
                            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                              Read article <ArrowRight size={15} />
                            </span>
                          </div>
                        </Link>
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function setMeta(name, content, attribute = "name") {
  if (!content) return;

  let el = document.head.querySelector(
    `meta[${attribute}="${name}"]`
  );

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }

  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');

  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }

  el.href = url;
}

function prepareArticleContent(html, resolveImageUrl) {
  if (typeof window === "undefined" || !html) {
    return { html, headings: [] };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const headings = [];
  const usedIds = new Set();

  doc.querySelectorAll("h1, h2, h3, h4").forEach((heading, index) => {
    const text = (heading.textContent || "").trim();
    if (!text) return;

    const original = (heading.id || text)
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    let base = original || `section-${index + 1}`;

    // Prefix IDs that start with a digit for consistent, selector-safe anchors.
    if (!/^[a-z]/.test(base)) {
      base = `section-${base}`;
    }

    let id = base;
    let suffix = 2;

    while (usedIds.has(id)) {
      id = `${base}-${suffix++}`;
    }

    usedIds.add(id);
    heading.id = id;

    headings.push({
      id,
      text,
      level: Number(heading.tagName.slice(1)),
    });
  });

  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src");

    if (src) {
      img.setAttribute("src", resolveImageUrl(src));
    }

    if (!img.hasAttribute("loading")) {
      img.setAttribute("loading", "lazy");
    }

    img.classList.add("my-6", "max-w-full", "rounded-xl");
  });

  doc.querySelectorAll("a[href^='#']").forEach((link) => {
    link.classList.add("text-brand", "underline", "underline-offset-2");
  });

  return { html: doc.body.innerHTML, headings };
}
